<script lang="ts" setup>
import { computed, onMounted, ref, toRaw, nextTick } from "vue";
import { DatePicker } from "v-calendar";
import "v-calendar/style.css";
import { useSearchStore } from "@/stores/search";
import { useScreens } from "vue-screen-utils";
import { DEFAULT_BREAKPOINT_DEFINITIONS } from "@/lib/util/breakpoint";
import { storeToRefs } from "pinia";
import { pushEventDataLayer } from "@/services/funnelEvent";
import type DateFunnelEvent from "@/types/funnel/dateFunnelEvent";
import { DATE } from "@/globalConsts/searchFunnelContentTypes";
import { SELECT_CONTENT } from "@/globalConsts/searchFunnelEvents";

const emit = defineEmits(["next"]);
const store = useSearchStore();
const { updateSelectedDateRange } = store;
const { mapCurrent } = useScreens({
    lapAndUp: DEFAULT_BREAKPOINT_DEFINITIONS.lapAndUp,
});
const activeMonths = 15;
const columns = mapCurrent({ lapAndUp: 2 }, 1);
const expanded = mapCurrent({ lapAndUp: false }, true);
const rows = mapCurrent({ lapAndUp: 1 }, activeMonths);
const today = new Date();
const dateRange = ref();
const date = new Date();
const maxDate = new Date(date.setMonth(date.getMonth() + activeMonths));
const lastPossibleDay = new Date(maxDate.getFullYear(), maxDate.getMonth(), 0);
const calendar = ref();
const masks = ref({
    modelValue: "YYYY-MM-DD",
});
const currentDateRange = computed(() => {
    return store.getDateRange;
});
const locale = computed(() => {
    return store.getLocale;
});
const { isPalm } = storeToRefs(store);
function updateDateRange() {
    if (dateRange.value.end && dateRange.value.start) {
        if (
            (currentDateRange.value.start !== toRaw(dateRange.value).start ||
                currentDateRange.value.end !== toRaw(dateRange.value).end) &&
            !isPalm.value
        ) {
            emit("next");
        }
        updateSelectedDateRange(dateRange.value);
        pushEventDataLayer({
            content_type: DATE,
            event: SELECT_CONTENT,
            max_date: Date.parse(dateRange.value.end ?? ""),
            min_date: Date.parse(dateRange.value.start ?? ""),
        } as DateFunnelEvent);
    }
}

async function moveToToday() {
    if (!calendar.value) return;

    await calendar.value.move(new Date());
}

/**
 * Initially set current dateRange to ref "dateRange"!
 * Cannot be moved to @onMounted as this would trigger an additional event.
 */
dateRange.value = currentDateRange.value;

onMounted(async () => {
    if (isPalm.value) {
        await nextTick(() => {
            moveToToday();
        });
    }
});
</script>

<template>
    <DatePicker
        ref="calendar"
        v-model.string.range="dateRange"
        :columns="columns"
        :rows="rows"
        :expanded="expanded"
        borderless
        :min-date="today"
        :max-date="lastPossibleDay"
        :step="1"
        :masks="masks"
        trim-weeks
        is-inline
        @update:modelValue="updateDateRange"
        :locale="locale"
    />
</template>
